import React from 'react';
import { Box, Typography, Card, Grid2, Tooltip, Button } from '@mui/material';
// import { ReactComponent as EditIcon } from '../../assets/SVG/aiBlueEdit.svg';
import { useTranslation } from 'react-i18next';

export interface EmbryoInformationDetailsProps {
  score?: number;
  scoreLabel?: string;
  grades?: {
    grade?: string | number;     // Inner Cell Mass
    icm?: string | number;      // Trophectoderm  
    te?: string | number; // Expansion grade
    embryoId?: string;
  };
  onEdit?: () => void;
  className?: string;
}


const headerSx = {
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '145%',
  letterSpacing: 0,
  color: '#353535',
  textTransform: 'uppercase',
};

const scoreSx = {
  fontFamily: 'Montserrat',
  fontWeight: 800,
  fontSize: '24px',
  lineHeight: '1',
  textAlign: 'center',
  marginBottom: 1,
  background: 'linear-gradient(103.98deg,rgba(240, 154, 89, 0.9) 0%, rgba(165, 65, 223, 0.9) 50.5%, rgba(57, 159, 197, 0.9) 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const scoreLabelSx = {
  fontFamily: 'Montserrat',
  fontWeight: 500,
  fontSize: '12px',
  color: '#232323',
  textAlign: 'center',
};

const gradeLabelSx = {
  fontFamily: 'Montserrat',
  fontWeight: 400,
  fontSize: '12px',
  color: '#9E9E9E',
  textAlign: 'center',
  marginBottom: 0.5
};

const gradeValueSx = {
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '20px',
  lineHeight: '1',
  color: '#232323',
  textAlign: 'center'
};


const gradeBoxSx ={
  textAlign: 'center',
  border: 1,
  borderColor: '#E0E0E0',
  p: 2,
  borderRadius: 4
};

const aiScoreSectionSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  mb: 2,
  borderRadius: 4,
  p: 3,
  background: 'linear-gradient(90deg, rgba(227, 202, 242, 0.2) 0%, rgba(243, 230, 185, 0.2) 33%, rgba(244, 206, 204, 0.2) 68.5%, rgba(191, 221, 242, 0.2) 100%)'
};

// const editIconSx={ 
//   padding: 0.5,
//   '&:hover': { backgroundColor: 'rgba(225, 126, 97, 0.1)' }
// };

const cardSx = {
  padding: 2,
  borderRadius: 4,
  backgroundColor: '#FFFFFF',
  border: '1px solid #F0F0F0',
  position: 'relative',
  width: '100%'
};


const editButtonSx = {
  borderRadius: 3,
  textTransform: 'none',
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '14px',
  backgroundColor: '#666666',
  '&:hover': {
    backgroundColor: '#555555',
  },
  padding: 0.7,
};


const EmbryoInformationDetails: React.FC<EmbryoInformationDetailsProps> = ({
  score = 0,
  scoreLabel = '',
  grades = {
    grade: '',
    icm: '',
    te: '',
    embryoId: ''
  },
  onEdit,
  className = ''
}) => {

  const { t: tAiGrading } = useTranslation('aiGrading_config');



  return (
    <Card
      elevation={0}
      className={className}
      sx={cardSx}
    >
      {/* Header with Edit Icon */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={headerSx}>
          {tAiGrading('embryoInformationDetails')}
        </Typography>
        {onEdit && (
          <Tooltip title={tAiGrading('edit')} arrow>
            {/* <IconButton 
              onClick={onEdit}
              sx={editIconSx}
            >
              <EditIcon width="20" height="20" />
            </IconButton> */}

          
            <Button variant="contained" sx={editButtonSx} onClick={onEdit}>
              {tAiGrading('edit')}
            </Button>
          
          </Tooltip>
        )}
      </Box>

      {/* AI Score Section */}
      <Box 
        sx={aiScoreSectionSx} >
        <Typography sx={scoreSx}>
          {typeof score === 'number' ? score.toFixed(2) : score}
        </Typography>
        <Typography sx={scoreLabelSx}>
          {scoreLabel}
        </Typography>
      </Box>

      {/* Grading Section */}
      <Grid2 container spacing={2} size={12}>
        {/* ICM Grade */}
        <Grid2 size={4}>
          <Box sx={gradeBoxSx}>
            <Typography sx={gradeValueSx}>
              {grades.grade}
            </Typography>
            <Typography sx={gradeLabelSx}>
              {tAiGrading('icmGrade')}
            </Typography>
          </Box>
        </Grid2>

        {/* TE Grade */}
        <Grid2 size={4}>
          <Box sx={gradeBoxSx}>
            <Typography sx={gradeValueSx}>
              {grades.icm}
            </Typography>
            <Typography sx={gradeLabelSx}>
              {tAiGrading('teGrade')}
            </Typography>
          </Box>
        </Grid2>

        {/* Expansion Grade */}
        <Grid2 size={4}>
          <Box sx={gradeBoxSx}>
            <Typography sx={gradeValueSx}>
              {grades.te}
            </Typography>
            <Typography sx={gradeLabelSx}>
              {tAiGrading('expansion')}
            </Typography>
          </Box>
        </Grid2>
      </Grid2>
    </Card>
  );
};

export default EmbryoInformationDetails;